import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { eachDayOfInterval, format, subDays } from "date-fns";
import { Activity } from "lucide-react";

import { pessoasQueries } from "@/services/api/queries/pessoas.queries";
import { PeriodoResumoAcessos } from "@/types/pessoa-acesso.type";
import { usePessoaResumoAcessos } from "@/hooks/pessoa/use-pessoa-resumo-acessos";
import { Button } from "@/components/ui/button";

import { AdocaoHero } from "./AdocaoHero";
import { AcessosPorDiaChart } from "./AcessosPorDiaChart";
import { CargosResumoList } from "./CargosResumoList";
import { PessoasEmFeriasList } from "./PessoasEmFeriasList";
import { PessoasOnlineList } from "./PessoasOnlineList";
import { PeriodoAcessoSelect } from "./PeriodoAcessoSelect";

export function PainelTab() {
  const [dias, setDias] = useState<PeriodoResumoAcessos>(30);
  const { resumo, loading, isError, refetch } = usePessoaResumoAcessos(dias);
  const { data: resumoPessoas } = useQuery(pessoasQueries.resumo());
  const today = new Date();
  const counts = new Map(resumo.acessosPorDia.map((day) => [day.dia, day.pessoas]));
  // A API omite dias sem acesso; o calendário completo evita inflar a média.
  const serie = eachDayOfInterval({ start: subDays(today, dias - 1), end: today }).map((date) => {
    const dia = format(date, "yyyy-MM-dd");
    return { dia, pessoas: counts.get(dia) ?? 0 };
  });

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <span className="rounded-xl bg-primary/10 p-2.5 text-primary">
          <Activity className="h-5 w-5" />
        </span>
        <div>
          <h2 className="text-lg font-semibold tracking-tight">A equipe em um olhar</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Acompanhe a participação, os acessos e quem está por aqui.
          </p>
        </div>
      </div>
      {isError ? (
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-destructive/30 bg-destructive/5 p-5"
        >
          <p className="text-sm">Não foi possível carregar os acessos da equipe.</p>
          <Button variant="outline" onClick={() => void refetch()}>
            Tentar novamente
          </Button>
        </div>
      ) : (
        <AdocaoHero resumo={resumo} emFerias={resumoPessoas?.emFerias} loading={loading} />
      )}
      <div className="grid items-start gap-4 lg:grid-cols-12">
        <section
          className="min-w-0 space-y-3 lg:col-span-8"
          aria-label="Análise de acessos"
          aria-busy={loading}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-sm font-semibold">Participação ao longo do tempo</h3>
            <PeriodoAcessoSelect value={dias} onChange={setDias} />
          </div>
          {loading && (
            <p role="status" className="text-sm text-muted-foreground">
              Carregando o histórico de acessos…
            </p>
          )}
          {!isError && (
            <AcessosPorDiaChart resumo={{ ...resumo, acessosPorDia: serie }} loading={loading} />
          )}
        </section>
        <div className="min-w-0 lg:col-span-4">
          <CargosResumoList />
        </div>
      </div>
      <section className="space-y-4" aria-label="Composição e presença da equipe">
        <div>
          <h3 className="text-sm font-semibold">Pessoas e presença</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Situação atual da equipe, independente do período selecionado.
          </p>
        </div>
        <div className="grid items-start gap-4 lg:grid-cols-2">
          <PessoasOnlineList />
          <PessoasEmFeriasList />
        </div>
      </section>
    </div>
  );
}
